import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'
import { parseUrlPathName } from '@corratech/pylot-tag-manager/src/utils/parseUrlPathName'
import { useTrackCheckoutStep } from '../hooks'
import { CheckoutStepNameEnum } from 'config'

export const CHECKOUT_PAGE_ROUTE = 'checkout'

/**
 * Unlike TrackUrlChange, here we track a transition to checkout page before it is complete
 * If checkout page is reloaded, we track the event after page view
 * TODO: confirm with SEO team whether it has to work this way.
 * On the one hand, 'checkout' event is supposed to happen when user actually navigates to checkout page
 * (when they click on Checkout button in the minicart, but we do not want to affect Cart code, if possible).
 * On the other hand, this behavior on 2 aforementioned cases is not consistent,
 * so we may have to change the approach later.
 */
export const useTrackCheckoutPageStep = (): void => {
    const { asPath, events, isFallback } = useRouter()

    const lastTrackeRef = useRef<string>()

    const trackCheckoutStep = useTrackCheckoutStep()

    const handleRouteChange = (path: string): void => {
        const pathName = parseUrlPathName(path)
        if (pathName.endsWith(CHECKOUT_PAGE_ROUTE)) {
            trackCheckoutStep({ step: CheckoutStepNameEnum.CART })
        }
    }

    // Handle transition to checkout page
    useEffect(() => {
        events.on('routeChangeStart', handleRouteChange)

        return () => {
            events.off('routeChangeStart', handleRouteChange)
        }
    }, [])

    // Handle loading on checkout page
    useEffect(() => {
        if (!isFallback) {
            handleRouteChange(asPath)
        }
        if (lastTrackeRef.current !== asPath) {
            return () => {
                events.off('routeChangeStart', handleRouteChange)
            }
        }
        lastTrackeRef.current = asPath
    }, [asPath])
}
